import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, KeyReturn, Eye, EyeSlash, Copy,
  Trash, PencilSimple, MicrosoftExcelLogo, WhatsappLogo, EnvelopeSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadCSV } from "@/lib/reportExport";
import SearchableCustomerSelect from "@/components/SearchableCustomerSelect";

const today = () => new Date().toISOString().slice(0, 10);

function emptyForm() {
  return {
    date: today(), customer_id: "", customer_name: "", company_name: "",
    user_id: "", password: "", mobile: "", email: "", portal: "", url: "", notes: "",
  };
}

function CredForm({ customers, portalOptions = [], editing, onClose, onSaved }) {
  const [f, setF] = useState(() => editing ? { ...emptyForm(), ...editing } : emptyForm());
  const [saving, setSaving] = useState(false);
  const [manual, setManual] = useState(!!editing?.customer_name && !editing?.customer_id);
  const [showPwd, setShowPwd] = useState(false);

  const pickCustomer = (id) => {
    if (id === "__manual__") { setManual(true); setF({ ...f, customer_id: "", customer_name: "" }); return; }
    setManual(false);
    const c = customers.find((x) => x.id === id);
    setF({ ...f, customer_id: id, customer_name: c?.name || "",
      mobile: c?.phone || f.mobile, email: c?.email || f.email });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!f.user_id.trim()) { toast.error("User ID is required"); return; }
    if (!f.password) { toast.error("Password is required"); return; }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.patch(`/credentials/${editing.id}`, f);
        toast.success("Credential updated");
      } else {
        await api.post("/credentials", f);
        toast.success("Credential saved");
      }
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Date *</Label>
          <Input type="date" required value={f.date}
            onChange={(e) => setF({ ...f, date: e.target.value })}
            className="font-mono" data-testid="cv-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Portal Name</Label>
          <Input value={f.portal}
            onChange={(e) => setF({ ...f, portal: e.target.value })}
            placeholder="Type new or pick from saved…"
            list="cv-portal-options"
            data-testid="cv-portal" />
          <datalist id="cv-portal-options">
            {portalOptions.map((p) => <option key={p} value={p} />)}
          </datalist>
          {portalOptions.length > 0 && (
            <div className="text-[10px] text-muted-foreground">
              {portalOptions.length} saved portal name{portalOptions.length > 1 ? "s" : ""} — click the input to see suggestions
            </div>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Customer *</Label>
        {!manual ? (
          <SearchableCustomerSelect
            value={f.customer_id}
            onChange={pickCustomer}
            customers={customers}
            createValue="__manual__"
            createLabel="Add manually…"
            placeholder="Choose customer"
            testid="cv-customer"
            getSubtitle={(c) => c.phone}
          />
        ) : (
          <div className="flex gap-2">
            <Input required value={f.customer_name}
              onChange={(e) => setF({ ...f, customer_name: e.target.value })}
              placeholder="Customer name" data-testid="cv-customer-name" />
            <Button type="button" variant="outline" className="h-9 px-2 text-[11px]"
              onClick={() => { setManual(false); setF({ ...f, customer_name: "" }); }}>
              ← List
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Company Name</Label>
          <Input value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })}
            placeholder="Business entity (if different from customer)" data-testid="cv-company" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Mobile Number</Label>
          <Input inputMode="tel" value={f.mobile}
            onChange={(e) => setF({ ...f, mobile: e.target.value })}
            placeholder="10-digit mobile" data-testid="cv-mobile" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Email</Label>
        <Input type="email" value={f.email}
          onChange={(e) => setF({ ...f, email: e.target.value })}
          placeholder="customer@example.com" data-testid="cv-email" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">User ID *</Label>
          <Input required value={f.user_id}
            onChange={(e) => setF({ ...f, user_id: e.target.value })}
            placeholder="Login username" className="font-mono"
            data-testid="cv-user-id" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Password *</Label>
          <div className="flex gap-1.5">
            <Input required type={showPwd ? "text" : "password"} value={f.password}
              onChange={(e) => setF({ ...f, password: e.target.value })}
              className="font-mono" data-testid="cv-password" />
            <Button type="button" variant="outline" size="icon" className="h-9 w-9"
              onClick={() => setShowPwd(!showPwd)} data-testid="cv-toggle-pwd">
              {showPwd ? <EyeSlash size={15} /> : <Eye size={15} />}
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Portal URL (optional)</Label>
        <Input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })}
          placeholder="https://…" className="font-mono text-[12px]" />
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Notes</Label>
        <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="cv-save-btn">
          {saving ? "Saving…" : editing?.id ? "Update Credential" : "Save Credential"}
        </Button>
      </div>
    </form>
  );
}

function PasswordCell({ value, testid }) {
  const [shown, setShown] = useState(false);
  const copy = () => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    toast.success("Password copied");
  };
  return (
    <div className="flex items-center gap-1">
      <span className="font-mono text-[12px]" data-testid={testid}>
        {value ? (shown ? value : "••••••••") : "—"}
      </span>
      {value && (
        <>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShown(!shown)}>
            {shown ? <EyeSlash size={12} /> : <Eye size={12} />}
          </Button>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={copy}>
            <Copy size={12} />
          </Button>
        </>
      )}
    </div>
  );
}

export default function CredentialsVault() {
  const { activeCompanyId, can, activeStoreId } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [portalFilter, setPortalFilter] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!activeCompanyId) return;
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
  }, [activeCompanyId, activeStoreId]);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ q, ...(start ? { start } : {}), ...(end ? { end } : {}) });
      const { data } = await api.get(`/credentials?${p}`);
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId, q, start, end]);

  const portalOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => { if (r.portal && r.portal.trim()) set.add(r.portal.trim()); });
    return Array.from(set).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!portalFilter || portalFilter === "all") return rows;
    return rows.filter((r) => (r.portal || "").trim() === portalFilter);
  }, [rows, portalFilter]);

  const del = async (id) => {
    if (!confirm("Delete this credential permanently?")) return;
    try { await api.delete(`/credentials/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const exportExcel = () => {
    downloadCSV(`credentials-vault-${new Date().toISOString().slice(0,10)}`, [
      { key: "date", label: "Date" },
      { key: "customer_name", label: "Customer" },
      { key: "company_name", label: "Company" },
      { key: "portal", label: "Portal" },
      { key: "user_id", label: "User ID" },
      { key: "password", label: "Password" },
      { key: "mobile", label: "Mobile" },
      { key: "email", label: "Email" },
      { key: "url", label: "URL" },
      { key: "notes", label: "Notes" },
    ], filteredRows);
  };

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Vault</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">User ID / Password Data</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Keep customer login credentials (GST portal, Income Tax, DGFT, banks) safely at a single click.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-9" onClick={exportExcel} data-testid="cv-export">
            <MicrosoftExcelLogo size={16} className="mr-1.5" /> Excel
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="h-9" data-testid="cv-new-btn" onClick={() => setEditing(null)}>
                <Plus size={15} weight="bold" className="mr-1.5" /> New Credential
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <KeyReturn size={20} /> {editing ? "Edit Credential" : "Add Customer Credential"}
                </DialogTitle>
              </DialogHeader>
              <CredForm customers={customers} portalOptions={portalOptions} editing={editing}
                onClose={() => { setOpen(false); setEditing(null); }} onSaved={load} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="card-flat p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1.5 min-w-[220px] flex-1">
          <Label className="eyebrow">Search</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by customer, mobile number or email…" data-testid="cv-search" />
        </div>
        <div className="space-y-1.5 min-w-[180px]">
          <Label className="eyebrow">Portal</Label>
          <Select value={portalFilter} onValueChange={setPortalFilter}>
            <SelectTrigger data-testid="cv-portal-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All portals</SelectItem>
              {portalOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">From</Label>
          <Input type="date" min="1900-01-01" value={start} onChange={(e) => setStart(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">To</Label>
          <Input type="date" min="1900-01-01" value={end} onChange={(e) => setEnd(e.target.value)} className="font-mono" />
        </div>
        <Button variant="outline" className="h-9" onClick={() => { setQ(""); setStart(""); setEnd(""); setPortalFilter("all"); }}>Reset</Button>
        <div className="text-[11px] text-muted-foreground ml-auto">
          Showing {filteredRows.length} credential{filteredRows.length !== 1 && "s"}
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Customer</th>
              <th>Company</th>
              <th>Portal</th>
              <th>User ID</th>
              <th>Password</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>Last Updated</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
            ) : filteredRows.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-10 text-muted-foreground">No credentials match the current filter.</td></tr>
            ) : filteredRows.map((r) => (
              <tr key={r.id} data-testid={`cv-row-${r.id}`}>
                <td className="font-mono text-[12px]">{fmtDate(r.date)}</td>
                <td className="font-medium">{r.customer_name}</td>
                <td>{r.company_name || <span className="text-muted-foreground">—</span>}</td>
                <td>
                  {r.portal ? (
                    r.url ? (
                      <a href={r.url} target="_blank" rel="noopener noreferrer"
                        className="text-primary underline text-[12.5px]">{r.portal}</a>
                    ) : <span className="text-[12.5px]">{r.portal}</span>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="font-mono text-[12px]">{r.user_id}</td>
                <td><PasswordCell value={r.password} testid={`cv-pwd-${r.id}`} /></td>
                <td className="font-mono text-[12px]">{r.mobile || "—"}</td>
                <td className="text-[12px] text-muted-foreground">{r.email || "—"}</td>
                <td className="font-mono text-[12px] text-muted-foreground" title={r.updated_at || r.created_at}>
                  {r.updated_at
                    ? fmtDateTime(r.updated_at)
                    : <span className="text-muted-foreground/70">Never</span>}
                </td>
                <td className="num" style={{ textAlign: "right" }}>
                  <div className="flex gap-1 justify-end items-center">
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      style={{ color: r.mobile ? "#128C7E" : "#94a3b8" }}
                      title={r.mobile ? `Send credential over WhatsApp → ${r.mobile}` : "No mobile on file — click Edit to add"}
                      onClick={() => {
                        const phone = String(r.mobile || "").replace(/[^0-9]/g, "");
                        if (!phone) { toast.error("No mobile on file. Click Edit and add one first."); return; }
                        const waPhone = phone.length === 10 ? `91${phone}` : phone;
                        const msg = [
                          `*${r.portal || 'Portal'} — Login Credentials*`,
                          "",
                          `Hi ${r.customer_name},`,
                          "",
                          `Your login credentials${r.portal ? ` for *${r.portal}*` : ""} are ready:`,
                          "",
                          r.url ? `🔗 URL: ${r.url}` : null,
                          `👤 User ID: \`${r.user_id}\``,
                          `🔑 Password: \`${r.password}\``,
                          "",
                          "⚠ Please change the password after first login and keep this message secure.",
                        ].filter(Boolean).join("\n");
                        window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
                      }}
                      data-testid={`cv-whatsapp-${r.id}`}>
                      <WhatsappLogo size={15} weight="fill" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8"
                      style={{ color: r.email ? undefined : "#94a3b8" }}
                      title={r.email ? `Email credential → ${r.email}` : "No email on file — click Edit to add"}
                      onClick={async () => {
                        if (!r.email) { toast.error("No email on file. Click Edit and add one first."); return; }
                        try {
                          const { data } = await api.post(`/credentials/${r.id}/email`, {});
                          toast.success(`Credential emailed to ${data.sent_to}`);
                        } catch (err) { toast.error(formatApiError(err)); }
                      }}
                      data-testid={`cv-email-${r.id}`}>
                      <EnvelopeSimple size={14} />
                    </Button>
                    {can("credentials", "edit") && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                        onClick={() => { setEditing(r); setOpen(true); }} data-testid={`cv-edit-${r.id}`}>
                        <PencilSimple size={13} />
                      </Button>
                    )}
                    {can("credentials", "delete") && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive"
                        onClick={() => del(r.id)}>
                        <Trash size={13} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
